import { useState } from 'react';
import { CheckCircle2, Loader2 } from 'lucide-react';
import type { ContactFormPayload } from '../api';

/**
 * The contact form, rendered inside the conversation rather than over the
 * composer.
 *
 * It used to replace the input area, which took the composer away: a visitor
 * who wanted to ask one more thing before handing over their details had to
 * dismiss the form to do it, and dismissing it was the only way back. As part
 * of the transcript instead, the form sits where it was offered, the composer
 * stays available, and scrolling back shows it in the place it belongs.
 *
 * That is also why there is no Cancel button. Cancel existed to give the
 * composer back; nothing is blocked now, so ignoring the form is simply typing
 * the next message — and a dismiss control that only hides a form the visitor
 * might still want is a way to lose a lead, not a courtesy.
 *
 * The fields are not hardcoded: they arrive on the `contact_form` event exactly
 * as the admin configured them. An unknown `field_type` renders as a text
 * input rather than being dropped, because a lead missing one answer is better
 * than a field nobody could fill in.
 */

export function ContactFormCard({
  contact,
  theme,
  primaryColor,
  values,
  onChange,
  onSubmit,
}: {
  contact: ContactFormPayload;
  theme: 'light' | 'dark';
  primaryColor: string;
  /**
   * What they have typed so far, held by the widget rather than here.
   *
   * Lifted out of this component so a half-filled form survives a refresh: a
   * visitor who typed their email and reloaded would otherwise find it gone,
   * and most do not type it a second time.
   */
  values: Record<string, string>;
  onChange: (values: Record<string, string>) => void;
  onSubmit: (values: Record<string, string>) => Promise<void>;
}) {
  const dark = theme === 'dark';
  const setValues = (fn: (prev: Record<string, string>) => Record<string, string>) =>
    onChange(fn(values));
  const [errors, setErrors] = useState<Record<string, string>>({});
  // Same reason as the option buttons: the brand colour is runtime config, so
  // a `focus:` class cannot reach it and the border is set inline instead.
  const [focused, setFocused] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);

  const fields = [...(contact.fields ?? [])].sort(
    (a, b) => (a.order || 0) - (b.order || 0)
  );

  const validate = () => {
    const found: Record<string, string> = {};
    for (const f of fields) {
      const v = (values[f.field_name] ?? '').trim();
      if (f.required && !v) {
        found[f.field_name] = `${f.field_label} is required.`;
        continue;
      }
      if (!v) continue;
      // Only shapes worth catching before a lead is written — anything
      // stricter rejects real addresses and numbers.
      if (f.field_type === 'email' && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)) {
        found[f.field_name] = 'Enter a valid email address.';
      }
      if (f.field_type === 'tel' && !/^[+\d][\d\s()-]{5,}$/.test(v)) {
        found[f.field_name] = 'Enter a valid phone number.';
      }
    }
    return found;
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (saving || done) return;

    const found = validate();
    setErrors(found);
    if (Object.keys(found).length > 0) return;

    setSaving(true);
    try {
      await onSubmit(values);
      setDone(true);
    } finally {
      setSaving(false);
    }
  };

  const border = dark ? 'vw-border-gray-700' : 'vw-border-gray-200';
  const surface = dark ? 'vw-bg-gray-800' : 'vw-bg-white';
  const labelColor = dark ? 'vw-text-gray-200' : 'vw-text-gray-700';
  const inputClass = `vw-w-full vw-px-3 vw-py-2 vw-rounded-[12px] vw-border vw-outline-none vw-transition-all vw-text-sm ${
    dark
      ? 'vw-bg-gray-700 vw-border-gray-600 vw-text-white vw-placeholder-gray-400'
      : 'vw-bg-gray-50 vw-border-gray-200 vw-text-gray-900 vw-placeholder-gray-400'
  }`;

  if (done) {
    // A receipt, not a form. Re-rendering the inputs after a successful submit
    // invites a second identical lead and makes it look like nothing happened.
    return (
      <div
        className={`vw-mt-2 vw-flex vw-items-center vw-gap-2 vw-rounded-[17px] vw-border ${border} ${surface} vw-px-4 vw-py-3`}
      >
        <CheckCircle2 className="vw-h-4 vw-w-4" style={{ color: primaryColor }} />
        <span className={`vw-text-sm ${labelColor}`}>
          Thanks — we have your details and will be in touch.
        </span>
      </div>
    );
  }

  return (
    <form
      onSubmit={submit}
      className={`vw-mt-2 vw-space-y-3 vw-rounded-[17px] vw-border ${border} ${surface} vw-p-4`}
    >
      {fields.map((field) => {
        const lit = focused === field.field_name;
        const ring = lit
          ? { borderColor: primaryColor, boxShadow: `0 0 0 1px ${primaryColor}` }
          : undefined;
        return (
        <div key={field.field_name}>
          <label className={`vw-block vw-text-sm vw-font-medium vw-mb-1 ${labelColor}`}>
            {field.field_label}
            {field.required && <span className="vw-text-red-500 vw-ml-1">*</span>}
          </label>
          {field.field_type === 'textarea' ? (
            <textarea
              rows={3}
              value={values[field.field_name] ?? ''}
              placeholder={field.placeholder}
              disabled={saving}
              style={ring}
              onFocus={() => setFocused(field.field_name)}
              onBlur={() => setFocused(null)}
              onChange={(e) =>
                setValues((p) => ({ ...p, [field.field_name]: e.target.value }))
              }
              className={inputClass}
            />
          ) : (
            <input
              // Only email/tel get a special type: the right mobile keyboard
              // and browser validation. Anything unrecognised is a text input.
              type={
                field.field_type === 'email' || field.field_type === 'tel'
                  ? field.field_type
                  : 'text'
              }
              value={values[field.field_name] ?? ''}
              placeholder={field.placeholder}
              disabled={saving}
              style={ring}
              onFocus={() => setFocused(field.field_name)}
              onBlur={() => setFocused(null)}
              onChange={(e) =>
                setValues((p) => ({ ...p, [field.field_name]: e.target.value }))
              }
              className={inputClass}
            />
          )}
          {errors[field.field_name] && (
            <p className="vw-mt-1 vw-text-xs vw-text-red-500">
              {errors[field.field_name]}
            </p>
          )}
        </div>
        );
      })}

      <button
        type="submit"
        disabled={saving}
        style={{ backgroundColor: primaryColor }}
        className="vw-w-full vw-flex vw-items-center vw-justify-center vw-gap-2 vw-rounded-[12px] vw-px-4 vw-py-2.5 vw-text-sm vw-font-medium vw-text-white vw-transition-all hover:vw-opacity-90 disabled:vw-opacity-50"
      >
        {saving && <Loader2 className="vw-h-4 vw-w-4 vw-animate-spin" />}
        {saving ? 'Sending…' : 'Submit'}
      </button>
    </form>
  );
}
